import React, { useState, useEffect, useMemo } from 'react';
import { useApi } from '../hooks/useApi';
import { toast } from 'react-hot-toast';
import { roleBadgeClass, roleLabel } from '../utils/permissions';
import { errorText } from '../utils/apiErrors';

const AdminLoginHistory = ({ googleIdToken, t }) => {
    const [logins, setLogins] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Default to last 6 months
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setMonth(d.getMonth() - 6);
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState('');

    const { apiRequest } = useApi(googleIdToken, () => window.location.reload());

    useEffect(() => {
        const fetchLoginHistory = async () => {
            setIsLoading(true);
            try {
                let url = `/api/admin/login-history?limit=10000`;
                if (startDate) url += `&startDate=${startDate}`;
                if (endDate) url += `&endDate=${endDate}`;
                const data = await apiRequest(url);
                if (data && data.logins) {
                    setLogins(data.logins);
                } else if (data && data.error) {
                    toast.error(errorText(data, t, t?.failed_load_login_history || "Failed to load login history."));
                }
            } catch (error) {
                console.error("Error fetching login history:", error);
                toast.error("Failed to load login history.");
            } finally {
                setIsLoading(false);
            }
        };

        if (googleIdToken) {
            fetchLoginHistory();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [googleIdToken, startDate, endDate]);

    const groupedLogins = useMemo(() => {
        const groups = {};
        const result = [];
        const lowerSearchTerm = searchTerm.toLowerCase();

        logins.filter(login => {
            if (!searchTerm) return true;
            return (login.name || '').toLowerCase().includes(lowerSearchTerm) ||
                (login.email || '').toLowerCase().includes(lowerSearchTerm);
        }).forEach(login => {
            if (!login.timestamp) return;
            const dateObj = new Date(login.timestamp);
            const dateStr = dateObj.toLocaleDateString();
            const email = login.email || 'unknown';
            const key = `${dateStr}_${email}`;

            if (!groups[key]) {
                groups[key] = { ...login, count: 1 };
                result.push(groups[key]);
            } else {
                groups[key].count += 1;
            }
        });
        return result;
    }, [logins, searchTerm]);

    const filteredLogins = useMemo(() => {
        const lowerSearchTerm = searchTerm.toLowerCase();
        return logins.filter(login => {
            if (!searchTerm) return true;
            return (login.name || '').toLowerCase().includes(lowerSearchTerm) ||
                (login.email || '').toLowerCase().includes(lowerSearchTerm);
        });
    }, [logins, searchTerm]);

    const loginCountsByDate = useMemo(() => {
        const counts = {};
        filteredLogins.forEach(login => {
            if (!login.timestamp) return;
            const dateObj = new Date(login.timestamp);
            const year = dateObj.getFullYear();
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;
            counts[dateStr] = (counts[dateStr] || 0) + 1;
        });
        return counts;
    }, [filteredLogins]);

    const calendarData = useMemo(() => {
        const end = endDate ? new Date(endDate) : new Date();
        end.setHours(23, 59, 59, 999);

        const endDayOfWeek = end.getDay();
        const daysToSaturday = 6 - endDayOfWeek;
        const calendarEnd = new Date(end);
        calendarEnd.setDate(calendarEnd.getDate() + daysToSaturday);

        const days = [];
        const totalDays = 53 * 7;

        for (let i = totalDays - 1; i >= 0; i--) {
            const d = new Date(calendarEnd);
            d.setDate(d.getDate() - i);

            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const dayVal = String(d.getDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${dayVal}`;

            const count = loginCountsByDate[dateStr] || 0;

            days.push({
                date: d,
                dateStr,
                count
            });
        }

        const weeks = [];
        for (let i = 0; i < 53; i++) {
            weeks.push(days.slice(i * 7, (i + 1) * 7));
        }

        return weeks;
    }, [endDate, loginCountsByDate]);

    const weeksWithLabels = useMemo(() => {
        let lastMonth = -1;
        return calendarData.map((week, index) => {
            const firstDay = week[0].date;
            const currentMonth = firstDay.getMonth();
            let showMonthLabel = false;

            if (index === 0 || currentMonth !== lastMonth) {
                showMonthLabel = true;
                lastMonth = currentMonth;
            }

            const monthName = firstDay.toLocaleString(t?.less ? 'nl' : 'en', { month: 'short' });

            return {
                week,
                showMonthLabel,
                monthName
            };
        });
    }, [calendarData, t]);

    if (isLoading) {
        return (
            <div className="p-4 sm:p-8 space-y-8 select-none pointer-events-none">
                {/* Header Skeleton */}
                <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center mb-6 gap-4 animate-pulse">
                    <div>
                        <div className="flex items-center">
                            <div className="h-8 w-8 mr-3 bg-gray-200 rounded-full flex-shrink-0"></div>
                            <div className="h-8 bg-gray-200 rounded w-48"></div>
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full xl:w-auto mt-4 xl:mt-0">
                        <div className="h-10 bg-gray-200 rounded-lg w-full sm:w-72"></div>
                        <div className="h-10 bg-gray-200 rounded-lg w-full sm:w-64"></div>
                    </div>
                </div>

                {/* Heatmap Card Skeleton */}
                <div className="bg-transparent border-none p-0 shadow-none animate-pulse">
                    <div className="flex flex-col space-y-4">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 border-b border-gray-100 pb-4">
                            <div className="space-y-2">
                                <div className="h-5 bg-gray-200 rounded w-48"></div>
                                <div className="h-3 bg-gray-200 rounded w-32"></div>
                            </div>
                            {/* Legend Skeleton */}
                            <div className="flex items-center gap-1.5 text-xs select-none">
                                <div className="w-10 h-3 bg-gray-100 rounded"></div>
                                <div className="w-3 h-3 rounded-sm bg-gray-100 border border-gray-200/50"></div>
                                <div className="w-3 h-3 rounded-sm bg-gray-200/30"></div>
                                <div className="w-3 h-3 rounded-sm bg-gray-200/50"></div>
                                <div className="w-3 h-3 rounded-sm bg-gray-200/70"></div>
                                <div className="w-3 h-3 rounded-sm bg-gray-200"></div>
                                <div className="w-10 h-3 bg-gray-100 rounded"></div>
                            </div>
                        </div>
                        
                        {/* Heatmap Grid Shimmer */}
                        <div className="overflow-x-auto pb-2 scrollbar-thin">
                            <div className="flex py-2 items-start w-full justify-between min-w-[800px] gap-1">
                                {/* Days Label Column */}
                                <div className="flex flex-col gap-1 pr-2 select-none">
                                    <div className="h-4" /> {/* Month label height placeholder */}
                                    <div className="h-3 text-[9px] text-gray-300 font-medium select-none flex items-center justify-end leading-none">Sun</div>
                                    <div className="h-3 text-[9px] text-gray-300 font-medium select-none flex items-center justify-end leading-none">Mon</div>
                                    <div className="h-3 text-[9px] text-gray-300 font-medium select-none flex items-center justify-end leading-none">Tue</div>
                                    <div className="h-3 text-[9px] text-gray-300 font-medium select-none flex items-center justify-end leading-none">Wed</div>
                                    <div className="h-3 text-[9px] text-gray-300 font-medium select-none flex items-center justify-end leading-none">Thu</div>
                                    <div className="h-3 text-[9px] text-gray-300 font-medium select-none flex items-center justify-end leading-none">Fri</div>
                                    <div className="h-3 text-[9px] text-gray-300 font-medium select-none flex items-center justify-end leading-none">Sat</div>
                                </div>

                                {/* 53 Weeks Grid */}
                                <div className="flex flex-row justify-between flex-1 gap-[2px]">
                                    {[...Array(53)].map((_, weekIdx) => (
                                        <div key={weekIdx} className="flex flex-col gap-1 flex-1 items-center">
                                            <div className="h-4 text-[9px] select-none overflow-visible whitespace-nowrap leading-none relative w-full">
                                                {/* empty month placeholder */}
                                            </div>
                                            {[...Array(7)].map((_, dayIdx) => (
                                                <div key={dayIdx} className="w-3 h-3 bg-gray-100 border border-gray-200/50 rounded-sm"></div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table Skeleton */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200 animate-pulse">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-primary">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.date || 'Date'}</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.name || 'Name'}</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.email || 'Email'}</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.role || 'Role'}</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {[...Array(6)].map((_, idx) => (
                                    <tr key={idx}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="h-4 bg-gray-200 rounded w-24"></div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap font-medium">
                                            <div className="h-4 bg-gray-200 rounded w-32"></div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <div className="w-7 h-7 rounded-full bg-gray-200 flex-shrink-0"></div>
                                                <div className="h-4 bg-gray-200 rounded w-48"></div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="h-5 bg-gray-200 rounded-full w-24"></div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-8 space-y-8">
            <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-3 bg-primary text-white rounded-full p-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        {t?.admin_login_history || 'Login History'}
                    </h1>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full xl:w-auto mt-4 xl:mt-0">
                    {/* Date Filters */}
                    <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 bg-white w-full sm:w-auto overflow-hidden">
                        <svg className="h-5 w-5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full sm:w-32 py-2 focus:outline-none text-sm text-gray-600 bg-transparent"
                            title="Start Date"
                        />
                        <span className="text-gray-300">-</span>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full sm:w-32 py-2 focus:outline-none text-sm text-gray-600 bg-transparent"
                            title="End Date"
                        />
                    </div>

                    <div className="relative w-full sm:w-64">
                        <input
                            type="text"
                            placeholder={t?.search_customer || 'Search...'}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                        <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                        </svg>
                    </div>
                </div>
            </div>

            {/* Login Calendar Overview */}
            <div className="bg-transparent border-none p-0 shadow-none">
                <div className="flex flex-col space-y-4">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 border-b border-gray-100 pb-4">
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {t?.login_overview || 'Login Calendar Overview'}
                            </h3>
                            <p className="text-xs text-gray-500 mt-1">
                                <span className="font-semibold text-gray-700">{filteredLogins.length}</span> {t?.logins_recorded || 'logins recorded in the active period'}
                            </p>
                        </div>
                        {/* Legend */}
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 select-none">
                            <span>{t?.less || 'Less'}</span>
                            <div className="w-3 h-3 rounded-sm bg-gray-100 border border-gray-200/50"></div>
                            <div className="w-3 h-3 rounded-sm bg-primary/15"></div>
                            <div className="w-3 h-3 rounded-sm bg-primary/40"></div>
                            <div className="w-3 h-3 rounded-sm bg-primary/70"></div>
                            <div className="w-3 h-3 rounded-sm bg-primary"></div>
                            <span>{t?.more || 'More'}</span>
                        </div>
                    </div>

                    {/* Heatmap Grid Wrapper */}
                    <div className="overflow-x-auto pb-2 scrollbar-thin">
                        <div className="flex select-none py-2 items-start w-full justify-between min-w-[800px] gap-1">
                            {/* Days Label Column */}
                            <div className="flex flex-col gap-1 pr-2 select-none">
                                <div className="h-4" /> {/* Month label height placeholder */}
                                <div className="h-3 text-[9px] text-gray-400 font-medium select-none flex items-center justify-end leading-none">Sun</div>
                                <div className="h-3 text-[9px] text-gray-400 font-medium select-none flex items-center justify-end leading-none">Mon</div>
                                <div className="h-3 text-[9px] text-gray-400 font-medium select-none flex items-center justify-end leading-none">Tue</div>
                                <div className="h-3 text-[9px] text-gray-400 font-medium select-none flex items-center justify-end leading-none">Wed</div>
                                <div className="h-3 text-[9px] text-gray-400 font-medium select-none flex items-center justify-end leading-none">Thu</div>
                                <div className="h-3 text-[9px] text-gray-400 font-medium select-none flex items-center justify-end leading-none">Fri</div>
                                <div className="h-3 text-[9px] text-gray-400 font-medium select-none flex items-center justify-end leading-none">Sat</div>
                            </div>

                            {/* Weeks Columns */}
                            <div className="flex flex-row justify-between flex-1 gap-[2px]">
                                {weeksWithLabels.map(({ week, showMonthLabel, monthName }, weekIndex) => (
                                    <div key={weekIndex} className="flex flex-col gap-1 flex-1 items-center">
                                        {/* Month Label */}
                                        <div className="h-4 text-[9px] text-gray-400 font-medium select-none overflow-visible whitespace-nowrap leading-none relative w-full">
                                            {showMonthLabel && (
                                                <span className="absolute left-0 -top-1">{monthName}</span>
                                            )}
                                        </div>

                                        {/* 7 Day Squares */}
                                        {week.map((day) => {
                                            const dateFormatted = day.date.toLocaleDateString(undefined, {
                                                weekday: 'long',
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            });
                                            const tooltipText = t?.less
                                                ? (day.count === 1 ? `1 inlog op ${dateFormatted}` : `${day.count} inlogs op ${dateFormatted}`)
                                                : (day.count === 1 ? `1 login on ${dateFormatted}` : `${day.count} logins on ${dateFormatted}`);

                                            return (
                                                <div
                                                    key={day.dateStr}
                                                    className={`w-3 h-3 rounded-sm transition-all duration-150 hover:scale-125 hover:shadow-sm cursor-pointer ${day.count === 0 ? 'bg-gray-100 border border-gray-200/50 hover:bg-gray-200' :
                                                        day.count === 1 ? 'bg-primary/15 hover:bg-primary/25' :
                                                            day.count <= 4 ? 'bg-primary/40 hover:bg-primary/50' :
                                                                day.count <= 9 ? 'bg-primary/70 hover:bg-primary/80' : 'bg-primary hover:bg-primary-hover'
                                                        }`}
                                                    title={tooltipText}
                                                />
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-primary">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.date || 'Date'}</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.name || 'Name'}</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.email || 'Email'}</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.role || 'Role'}</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {groupedLogins.length > 0 ? (
                                groupedLogins.map((login, index) => (
                                    <tr key={login.id || index} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                            {login.timestamp ? new Date(login.timestamp).toLocaleString() : 'N/A'}
                                            {login.count > 1 && (
                                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                    {login.count} logins
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {login.name || 'Unknown'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div className="flex items-center gap-2">
                                                {login.picture && (
                                                    <img
                                                        src={login.picture}
                                                        alt={login.name || 'Avatar'}
                                                        className="w-7 h-7 rounded-full object-cover border border-gray-200"
                                                        referrerPolicy="no-referrer"
                                                    />
                                                )}
                                                <span>{login.email || 'N/A'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                                            {/* A role -> colour map rather than an
                                                internal_admin ternary: there are now four
                                                internal roles, and rows written before the
                                                allowlist existed still carry the old
                                                vocabulary, which the map also covers. A
                                                refused sign-in has no role at all. */}
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${roleBadgeClass(login.role)}`}>
                                                {roleLabel(login.role)}
                                            </span>
                                            {login.outcome && login.outcome !== 'allowed' && (
                                                <span
                                                    className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 border border-red-200"
                                                    title={login.outcome}
                                                >
                                                    {t?.login_refused || 'Refused'}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-8 text-center text-sm text-gray-500">
                                        {t?.no_data_found || 'No data found.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminLoginHistory;
